/* eslint-disable id-length */
import { useEffect, useMemo } from "react";
import { ChartDataset } from "chart.js";
import LineChart, { LineChartUnit } from "~/components/ClustersInsights/LineChart";
import { colorsForIndex } from "~/components/ClustersInsights/Theme";
import { stackedChart } from "~/components/ClustersInsights/chartOptions";
import { useChart } from "~/components/shared/Chart/chart";
import { Series } from "~/components/job/Job/HostedCompute/types";
import {
  chartOptionsOverride,
  convertToChartDatasets,
  tooltipValueFormat,
} from "~/components/job/Job/HostedCompute/common";

interface MemoryComputeChartProps {
  chartData: Series;
}

export const MemoryComputeChart = (props: MemoryComputeChartProps) => {
  const data = useMemo(() => {
    const datasets = convertToChartDatasets(props.chartData)
      .map((dataset) => {
        const data = (dataset.data as number[]).map((value) => value / 1024 ** 3);

        switch (dataset.label) {
          case "mem_available":
            return { ...dataset, data, label: "Available Memory", order: 1 };
          case "mem_used":
            return { ...dataset, data, label: "Used Memory", order: 0 };
          default:
            return { ...dataset, data };
        }
      })
      .map((dataset, index) => ({ ...dataset, ...colorsForIndex(index) }));

    return {
      labels: props.chartData.timestamps,
      datasets: datasets as ChartDataset<"line", number[]>[],
    };
  }, [props.chartData]);

  const { chartRef, updateData } = useChart<"line", number>();

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <LineChart
      ref={chartRef}
      data={data}
      units={{ y: LineChartUnit.gigabytes }}
      options={chartOptionsOverride(
        stackedChart,
        tooltipValueFormat((value) => `${value.toFixed(2)} GB`),
      )}
    />
  );
};
